/**
 * Public Legal page — renders the tenant's effective Terms & Conditions
 * OR Privacy Policy as Markdown.
 *
 * Drives two routes:
 *   ``/legal/terms``    → kind="terms"
 *   ``/legal/privacy``  → kind="privacy"
 *
 * Content cascade (backend ``GET /api/policies``):
 *   tenant override → platform default → hardcoded Swiggy-style template.
 *
 * Placeholders ({company_name}, {support_email}, etc.) are already
 * substituted server-side.
 *
 * Tenant resolution (iter68 — required for Play Store / App Store
 * submissions). The page is served from the platform apex
 * ``marketplace.localappstore.in``, so the regular host-based resolver
 * has no way to know which tenant to render policies for. The store
 * submission URLs MUST therefore carry an explicit tenant slug:
 *
 *   https://marketplace.localappstore.in/legal/privacy?tenant=<slug>
 *   https://marketplace.localappstore.in/legal/terms?tenant=<slug>
 *
 * The resolver inside this page:
 *   1. ``?tenant=<slug>`` query param  → authoritative for THIS request.
 *      Sent as an explicit X-Tenant-Slug header AND NOT persisted to
 *      localStorage (would otherwise leak across tabs / pollute the
 *      next visit to a different tenant's pages).
 *   2. Tenant subdomain in host (e.g. ``bizil.marketplace.localappstore.in``)
 *      → still authoritative via the default resolver.
 *   3. Fallback to the persisted slug (legacy behaviour).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Loader2 } from "lucide-react";
import { api, resolveTenantSlug } from "../lib/api";

export default function LegalPage({ kind }) {
    const [doc, setDoc] = useState(null);
    const [err, setErr] = useState(null);
    const nav = useNavigate();
    const [params] = useSearchParams();

    // iter68 — Explicit ``?tenant=`` ALWAYS wins on the legal page so
    // store reviewers (Play Store / App Store) get the right content
    // even from a freshly-opened browser. Falls back to the default
    // resolver (host / persisted) for in-app navigation where no
    // query param is set.
    const overrideSlug = (params.get("tenant") || "").trim().toLowerCase();
    // iter70 — ``?app=`` distinguishes the Customer vs Rider build's
    // submission URL. Forwarded to the backend via ``X-App-Code`` so
    // it can later return app-specific markdown (today returns the
    // same content for both apps — keeps URLs unique per submission).
    const appCode = (params.get("app") || "").trim().toLowerCase();
    const effectiveSlug = useMemo(
        () => overrideSlug || resolveTenantSlug(),
        [overrideSlug],
    );

    useEffect(() => {
        // The axios interceptor sets ``X-Tenant-Slug`` from
        // ``resolveTenantSlug()`` — for the legal page we override that
        // header with the URL slug so the request never picks up a
        // stale persisted tenant. Per-request override; does not touch
        // storage at all (no pollution of the active tenant in other
        // tabs — iter146 made this the default for sessionStorage too,
        // but this explicit override remains useful within a single
        // tab that bounces between multiple tenants' legal pages).
        const headers = { "X-Tenant-Slug": effectiveSlug };
        if (appCode) headers["X-App-Code"] = appCode;
        api.get("/policies", { headers })
            .then(({ data }) => setDoc(data))
            .catch((e) => setErr(e?.response?.data?.detail || "Could not load"));
    }, [effectiveSlug, appCode, kind]);

    // iter71 — Sync document.title with the rendered policy. Lets store
    // reviewers verify they're on the right tenant + app variant just
    // by glancing at the browser tab. Restored on unmount.
    useEffect(() => {
        const prev = document.title;
        const base = kind === "privacy" ? "Privacy Policy" : "Terms & Conditions";
        const tenant = doc?.tenant_name ? ` · ${doc.tenant_name}` : (effectiveSlug ? ` · ${effectiveSlug}` : "");
        const app = appCode === "rider" ? " · Rider app" :
                    appCode === "customer" ? " · Customer app" : "";
        document.title = `${base}${app}${tenant}`;
        return () => { document.title = prev; };
    }, [kind, appCode, doc, effectiveSlug]);

    const md = doc ? (kind === "privacy" ? doc.privacy_md : doc.terms_md) : "";
    const baseTitle = kind === "privacy" ? "Privacy Policy" : "Terms & Conditions";
    // iter70 — Suffix the page title with the app label when set so
    // store reviewers can verify they're looking at the right doc.
    const title = appCode === "rider" ? `${baseTitle} · Rider app` :
                  appCode === "customer" ? `${baseTitle} · Customer app` :
                  baseTitle;

    return (
        <div className="min-h-screen bg-background" data-testid={`legal-page-${kind}`}>
            <header className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border">
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
                    <button
                        onClick={() => nav(-1)}
                        data-testid="legal-back"
                        className="p-2 -ml-2 rounded-full hover:bg-muted"
                    ><ArrowLeft size={18} /></button>
                    <h1 className="text-lg font-bold">{title}</h1>
                </div>
            </header>
            <main className="max-w-3xl mx-auto px-4 py-6">
                {err && <div className="text-sm text-red-600" data-testid="legal-error">{err}</div>}
                {!doc && !err && (
                    <div className="text-center py-12 text-muted-foreground">
                        <Loader2 size={20} className="inline animate-spin" />
                    </div>
                )}
                {doc && (
                    <article
                        data-testid={`legal-body-${kind}`}
                        data-tenant-id={doc.tenant_id || ""}
                        className="max-w-none text-sm leading-relaxed
                            [&_h1]:text-2xl [&_h1]:font-extrabold [&_h1]:tracking-tight [&_h1]:mb-3
                            [&_h2]:text-base [&_h2]:font-bold [&_h2]:mt-6 [&_h2]:mb-2
                            [&_h3]:text-sm [&_h3]:font-bold [&_h3]:mt-4 [&_h3]:mb-1.5
                            [&_p]:my-2 [&_p]:text-muted-foreground
                            [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-2 [&_ul]:text-muted-foreground
                            [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-2 [&_ol]:text-muted-foreground
                            [&_li]:my-1
                            [&_strong]:text-foreground [&_strong]:font-semibold
                            [&_em]:text-muted-foreground [&_em]:not-italic [&_em]:text-xs [&_em]:block [&_em]:mb-4
                            [&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs
                            [&_blockquote]:border-l-2 [&_blockquote]:border-primary [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-muted-foreground [&_blockquote]:my-3
                            [&_a]:text-primary [&_a]:underline"
                    >
                        <ReactMarkdown>{md}</ReactMarkdown>
                    </article>
                )}
            </main>
        </div>
    );
}
